import { SavingsReceipt } from "./savings";

function pmt(
  rate: number,
  nper: number,
  pv: number,
  fv: number = 0,
  type: number = 0,
): number {
  if (rate === 0) return -(pv + fv) / nper;
  return (
    (-(pv * Math.pow(1 + rate, nper) + fv) * rate) /
    ((1 + rate * type) * (Math.pow(1 + rate, nper) - 1))
  );
}

function ipmt(
  rate: number,
  per: number,
  nper: number,
  pv: number,
  fv: number = 0,
  type: number = 0,
): number {
  if (rate === 0) return 0;
  return (
    -(
      pv * Math.pow(1 + rate, per - 1) +
      (pmt(rate, nper, pv, fv, type) *
        (1 + rate * type) *
        (Math.pow(1 + rate, per - 1) - 1)) /
        rate
    ) * rate
  );
}

function nper(
  rate: number,
  pmt: number,
  pv: number,
  fv: number = 0,
  type: number = 0,
): number {
  if (rate === 0) {
    if (pmt === 0) throw new Error("Payment cannot be zero when rate is zero.");
    return -(pv + fv) / pmt;
  }
  const numerator = pmt * (1 + rate * type) - fv * rate;
  const denominator = pv * rate + pmt * (1 + rate * type);
  if (-pmt <= pv * rate) {
    throw new Error(
      `Monthly payment must be greater than [CUR]${pv * rate}[/CUR] to cover the interest.`,
    );
  }
  if (denominator === 0 || numerator / denominator <= 0)
    throw new Error("Invalid inputs for nper calculation.");
  return Math.log(numerator / denominator) / Math.log(1 + rate);
}

function rate(
  nper: number,
  pmt: number,
  pv: number,
  fv: number = 0,
  type: number = 0,
  guess: number = 0.1,
): number {
  const maxIter = 100;
  const tol = 1e-6;
  let rate = guess;

  for (let i = 0; i < maxIter; i++) {
    const f =
      pv * Math.pow(1 + rate, nper) +
      (pmt * (1 + rate * type) * (Math.pow(1 + rate, nper) - 1)) / rate +
      fv;
    const fPrime =
      pv * nper * Math.pow(1 + rate, nper - 1) +
      pmt *
        (1 + rate * type) *
        ((Math.pow(1 + rate, nper) - 1) / (rate * rate) -
          (nper * Math.pow(1 + rate, nper - 1)) / rate);

    const newRate = rate - f / fPrime;

    if (Math.abs(newRate - rate) < tol) {
      return newRate;
    }

    rate = newRate;
  }

  throw new Error("Rate calculation did not converge.");
}

class MortgageEntry {
  readonly month: number;
  readonly principal: number;
  readonly interestRate: number;
  readonly interestAmount: number;
  readonly termMonths: number;
  readonly dealId: number | undefined;
  readonly type: string;
  readonly monthlyPayment: number;
  amountPaid: number = 0.0;
  additionalCapitalRepayments: number = 0.0;
  additionalCapitalRepaymentReason: string | null = null;
  finalized: boolean = false;
  readonly savingsBalance: number | null = null;
  readonly savingsInterest: number | null = null;

  constructor(
    month: number,
    principal: number,
    interestRate: number,
    interestAmount: number,
    termMonths: number,
    monthlyPayment: number,
    interestOnly: boolean,
    dealId: number | undefined,
    savingsReceipt?: SavingsReceipt | null,
  ) {
    this.month = month;
    this.principal = principal;
    this.interestRate = interestRate;
    this.interestAmount = interestAmount;
    this.termMonths = termMonths;
    this.dealId = dealId;
    this.type = interestOnly ? "interest-only" : "repayment";
    this.monthlyPayment = monthlyPayment;

    if (savingsReceipt) {
      this.savingsBalance = savingsReceipt.balance;
      this.savingsInterest = savingsReceipt.interest;
    }
  }

  pay(amount: number): { additional_capital_repayment: number } {
    if (Math.round(amount * 100) / 100 !== amount) {
      throw new Error("Payment amount must be rounded to two decimal places.");
    }
    if (this.finalized) {
      throw new Error("Cannot pay on a finalized entry.");
    }
    if (amount <= 0) {
      throw new Error("Payment amount must be greater than zero.");
    }
    if (this.amountPaid + amount < this.monthlyPayment) {
      throw new Error("Payment is less than the required monthly payment.");
    }
    const additionalCapitalRepayment =
      Math.round(
        Math.max(0.0, this.amountPaid + amount - this.monthlyPayment) * 100,
      ) / 100;
    this.amountPaid = Math.round((this.amountPaid + amount) * 100) / 100;
    if (additionalCapitalRepayment > 0) {
      this.additionalCapitalRepayments =
        Math.round(
          (this.additionalCapitalRepayments + additionalCapitalRepayment) * 100,
        ) / 100;
    }
    return { additional_capital_repayment: additionalCapitalRepayment };
  }

  validate() {
    if (this.month <= 0) throw new Error("Month must be a positive integer.");
    if (this.principal <= 0)
      throw new Error("Principal must be a positive number.");
    if (this.interestRate < 0)
      throw new Error("Interest rate cannot be negative.");
    if (this.termMonths <= 0)
      throw new Error("Term months must be a positive integer.");
    if (this.monthlyPayment <= 0)
      throw new Error("Monthly payment must be greater than zero.");
  }
}

class MortgageLedger {
  entries: MortgageEntry[] = [];

  append(entry: MortgageEntry) {
    this.entries.push(entry);
  }

  get(index: number): MortgageEntry {
    return this.entries[index];
  }

  get length(): number {
    return this.entries.length;
  }

  currentYearEntries(month: number): MortgageEntry[] {
    const yearStartMonth = Math.floor((month - 1) / 12) * 12 + 1;
    return this.entries.filter(
      (entry) =>
        entry.month >= yearStartMonth && entry.month < yearStartMonth + 12,
    );
  }

  nAcrsWithType(additionalCapitalRepaymentType: string): number {
    return this.entries.filter(
      (entry) =>
        entry.additionalCapitalRepayments > 0 &&
        entry.additionalCapitalRepaymentReason ===
          additionalCapitalRepaymentType,
    ).length;
  }

  totalAcrAmount(): number {
    return this.entries.reduce<number>(
      (acc, entry) => acc + entry.additionalCapitalRepayments,
      0,
    );
  }
}

export default class MortgageAccount {
  private _principal: number;
  readonly termMonths: number;
  private _remainingMonths: number;
  readonly interestRate: number;
  readonly desiredMonthlyPayment: number;
  currentMortgageEntry: MortgageEntry | null = null;
  readonly ledger: MortgageLedger = new MortgageLedger();
  readonly additionalCapitalRepaymentPercentageLimit: number | undefined;
  readonly reduceTermOnAdditionalCapitalRepayment: boolean | undefined;
  readonly dealLengthMonths: number | undefined;
  dealId: number | undefined;
  readonly interestOnly: boolean;

  constructor(
    principal: number,
    termMonths: number | undefined,
    interestRate: number | undefined,
    monthlyPayment: number | undefined,
    dealLengthMonths: number | undefined,
    interestOnly: boolean,
    additionalCapitalRepaymentPercentageLimit: number | undefined,
    reduceTermOnAdditionalCapitalRepayment: boolean | undefined,
  ) {
    if (principal <= 0) throw new Error("Principal must be a positive number.");
    if (Math.round(principal * 100) / 100 !== principal)
      throw new Error("Principal must be rounded to two decimal places.");
    if (interestRate && interestRate <= 0)
      throw new Error("Interest rate must be a positive number.");
    if (monthlyPayment && monthlyPayment < 0)
      throw new Error("Monthly payment cannot be negative.");
    if (
      monthlyPayment &&
      Math.round(monthlyPayment * 100) / 100 !== monthlyPayment
    ) {
      throw new Error(
        "Desired monthly payment must be rounded to two decimal places.",
      );
    }
    if (
      additionalCapitalRepaymentPercentageLimit &&
      (additionalCapitalRepaymentPercentageLimit < 0 ||
        additionalCapitalRepaymentPercentageLimit > 100)
    ) {
      throw new Error(
        "Annual Overpayment Allowance must be between 0% and 100%.",
      );
    }

    this._principal = principal;

    const providedParams = [
      termMonths !== undefined,
      interestRate !== undefined,
      monthlyPayment !== undefined,
    ].filter((v) => v).length;
    if (providedParams !== 2) {
      throw new Error(
        "Exactly two of Mortgage Term, Interest Rate, and Monthly Payment must be provided.",
      );
    }

    if (termMonths === undefined) {
      if (interestRate === 0 && monthlyPayment! < principal / 12) {
        throw new Error(
          "With a 0% interest rate, the monthly payment must be at least principal divided by 12.",
        );
      }
      termMonths = nper(interestRate! / 12 / 100, -monthlyPayment!, principal);
      termMonths = Math.round(termMonths);
      if (termMonths <= 0)
        throw new Error("Calculated term months must be a positive integer.");
      if (termMonths === null)
        throw new Error("Calculated term months cannot be null.");
    } else if (interestRate === undefined) {
      if (termMonths <= 0)
        throw new Error("Term months must be a positive integer.");
      if (monthlyPayment! <= 0)
        throw new Error(
          "Monthly payment must be greater than zero to calculate interest rate.",
        );
      let calculatedRate: number;
      if (interestOnly) {
        calculatedRate = monthlyPayment! / this.principal;
      } else {
        calculatedRate = rate(termMonths, -monthlyPayment!, this.principal);
      }
      interestRate = Math.round(calculatedRate * 12 * 100 * 100) / 100;
      if (interestRate <= 0)
        throw new Error("Calculated interest rate must be a positive number.");
      if (interestRate < 0)
        throw new Error("Calculated interest rate cannot be negative.");
    } else if (monthlyPayment === undefined) {
      if (termMonths <= 0)
        throw new Error("Term months must be a positive integer.");
      if (interestRate < 0)
        throw new Error("Interest rate cannot be negative.");
      const monthlyRate = interestRate / 12 / 100;
      if (interestOnly) {
        // For interest-only, monthly payment is just the interest
        monthlyPayment = this.principal * monthlyRate;
      } else {
        monthlyPayment = pmt(monthlyRate, termMonths, -this.principal);
      }
    } else {
      throw new Error(
        "Exactly two of Mortgage Term, Interest Rate, and Monthly Payment must be provided.",
      );
    }

    this.termMonths = termMonths;
    this._remainingMonths = termMonths;
    this.interestRate = interestRate!;
    this.desiredMonthlyPayment = monthlyPayment!;
    this.interestOnly = interestOnly;

    this.additionalCapitalRepaymentPercentageLimit =
      additionalCapitalRepaymentPercentageLimit;
    this.reduceTermOnAdditionalCapitalRepayment =
      reduceTermOnAdditionalCapitalRepayment;
    if (dealLengthMonths !== undefined) {
      if (![12, 24, 36, 48, 60].includes(dealLengthMonths)) {
        throw new Error(
          "Deal length must be one of 12, 24, 36, 48, or 60 months.",
        );
      }
      this.dealLengthMonths = dealLengthMonths;
      this.dealId = 1;
    }
  }

  get principal() {
    return this._principal;
  }

  set principal(value: number) {
    if (value < 0) throw new Error("Principal cannot be negative.");
    if (Math.round(value * 100) / 100 !== value)
      throw new Error("Principal must be rounded to two decimal places.");
    this._principal = value;
  }

  get ipmt(): number {
    if (this.principal <= 0) return 0;
    if (this.remainingMonths <= 0)
      throw new Error(
        "Remaining months must be greater than zero to calculate interest payment.",
      );
    if (this.interestRate === 0) return 0;
    const interest = ipmt(
      this.interestRate / 12 / 100,
      1,
      this.remainingMonths,
      -this.principal,
    );
    return Math.round(interest * 100) / 100;
  }

  get pmt(): number {
    if (this.isInterestOnly) {
      return this.ipmt;
    }
    if (this.principal <= 0) return 0;
    if (this.remainingMonths <= 0)
      throw new Error(
        "Remaining months must be greater than zero to calculate payment.",
      );
    if (this.interestRate === 0) {
      const payment = this.principal / this.remainingMonths;
      return Math.round(payment * 100) / 100;
    }
    const payment = pmt(
      this.interestRate / 12 / 100,
      this.remainingMonths,
      -this.principal,
    );
    return Math.round(payment * 100) / 100;
  }

  get isInterestOnly(): boolean {
    if (this.desiredMonthlyPayment <= this.ipmt) return true;
    return this.interestOnly;
  }

  get remainingMonths(): number {
    return this._remainingMonths;
  }

  decrementRemainingMonths() {
    if (this._remainingMonths > 0) {
      this._remainingMonths -= 1;
    } else {
      throw new Error("Remaining months cannot be less than zero.");
    }
  }

  adjustRemainingMonthsForAdditionalRepayment() {
    if (this.reduceTermOnAdditionalCapitalRepayment && !this.isInterestOnly) {
      const newTerm = Math.ceil(
        nper(
          this.interestRate / 12 / 100,
          -this.currentMortgageEntry!.monthlyPayment,
          this.principal,
        ),
      );
      if (newTerm !== null && newTerm < this._remainingMonths) {
        this._remainingMonths = newTerm;
      } else {
        throw new Error(
          "New term months must be less than current remaining months.",
        );
      }
    }
  }

  get isBetweenFixedDeals(): boolean {
    if (this.dealLengthMonths === undefined) return false;
    return this.currentDealMonth === 0;
  }

  get currentMonth(): number {
    const latestEntry =
      this.currentMortgageEntry ||
      (this.ledger.length > 0 ? this.ledger.get(this.ledger.length - 1) : null);
    if (!latestEntry)
      throw new Error("No current mortgage entry or ledger entries available.");
    return latestEntry.month;
  }

  get currentYearlyCycleMonth(): number {
    return this.currentMonth % 12;
  }

  get currentDealMonth(): number {
    if (this.dealLengthMonths === undefined)
      throw new Error("Deal length is not defined.");
    return this.currentMonth % this.dealLengthMonths;
  }

  get dealDistributedAdditionalCapitalRepaymentMonths(): number[] {
    switch (this.dealLengthMonths) {
      case 12:
        return [6, 0];
      case 24:
        return [8, 16, 0];
      case 36:
        return [9, 18, 27, 0];
      case 48:
        return [10, 20, 30, 40, 0];
      case 60:
        return [10, 20, 30, 40, 50, 0];
      default:
        throw new Error(
          "Deal length not supported for distributed additional capital repayments.",
        );
    }
  }

  get additionalCapitalRepaymentAllowanceAmount(): number {
    if (this.additionalCapitalRepaymentPercentageLimit === undefined) return 0;
    if (this.isBetweenFixedDeals) return this.principal + this.ipmt;
    return (
      Math.round(
        this.principal * this.additionalCapitalRepaymentPercentageLimit,
      ) / 100
    ); // TODO: should interest be included?
  }

  get remainingAdditionalCapitalRepayments(): number {
    if (this.isBetweenFixedDeals) return 1;
    const additionalCapitalRepaymentsThisYear = this.ledger
      .currentYearEntries(this.currentMortgageEntry!.month)
      .filter((entry) => entry.additionalCapitalRepayments > 0).length;
    return 1 - additionalCapitalRepaymentsThisYear;
  }

  get remainingAdditionalCapitalRepaymentAmount(): number {
    if (this.isBetweenFixedDeals) return this.principal; //+ this.monthlyInterestPayment;
    const remaining = this.ledger
      .currentYearEntries(this.currentMortgageEntry!.month)
      .filter((entry) => entry.amountPaid > this.pmt)
      .reduce((acc, entry) => acc + entry.additionalCapitalRepayments, 0);
    return (
      Math.round(
        (this.additionalCapitalRepaymentAllowanceAmount - remaining) * 100,
      ) / 100
    );
  }

  startMonth(savingsReceipt?: SavingsReceipt | null) {
    if (this.currentMortgageEntry !== null)
      throw new Error("A month has already been started.");
    this.currentMortgageEntry = new MortgageEntry(
      this.ledger.length + 1,
      this.principal,
      this.interestRate,
      this.ipmt,
      this.remainingMonths,
      this.pmt,
      this.isInterestOnly,
      this.dealId,
      savingsReceipt,
    );
    this.currentMortgageEntry.validate();
  }

  pay(
    amount: number,
    additionalCapitalRepayment: boolean = false,
    additionalCapitalRepaymentReason?: string | null,
  ) {
    if (Math.round(amount * 100) / 100 !== amount)
      throw new Error("Payment amount must be rounded to two decimal places.");
    const receipt = this.currentMortgageEntry!.pay(amount);
    if (
      receipt.additional_capital_repayment > 0 !==
      additionalCapitalRepayment
    ) {
      throw new Error(
        "`additional_capital_repayment` flag does not match additional capital repayment.",
      );
    }
    if (additionalCapitalRepayment) {
      if (receipt.additional_capital_repayment !== amount) {
        throw new Error(
          "Additional capital repayment amount does not match the payment amount.",
        );
      }
      this.principal =
        Math.round(
          (this.principal - receipt.additional_capital_repayment) * 100,
        ) / 100;
    } else {
      this.principal =
        Math.round((this.principal + this.ipmt - amount) * 100) / 100;
    }
    if (receipt.additional_capital_repayment > 0) {
      if (!additionalCapitalRepaymentReason)
        throw new Error(
          "Additional capital repayment reason must be provided.",
        );
      this.currentMortgageEntry!.additionalCapitalRepaymentReason =
        additionalCapitalRepaymentReason;
      // TODO: reimplement this for new setup
      // if (!this.isInterestOnly) { // can't switch from interest-only to repayment mid-deal
      //     this.principal -= receipt.additional_capital_repayment;
      //     // this.adjustRemainingMonthsForAdditionalRepayment(); // TODO: should this be added?
      // }
    }
  }

  endMonth() {
    if (this.currentMortgageEntry === null)
      throw new Error("No month has been started.");
    if (this.currentMortgageEntry.finalized)
      throw new Error("The current month has already been finalized.");
    this.currentMortgageEntry.validate();
    if (
      this.currentMortgageEntry.amountPaid <
      this.currentMortgageEntry.monthlyPayment
    ) {
      throw new Error("Amount paid is less than the required monthly payment.");
    }
    this.currentMortgageEntry.finalized = true;
    this.ledger.append(this.currentMortgageEntry);
    this.currentMortgageEntry = null;

    // Decrement remaining months BEFORE adjusting term (only if principal still positive)
    if (this.principal > 0) {
      this.decrementRemainingMonths();
    }

    if (this.isBetweenFixedDeals) {
      this.dealId! += 1;
      // When between deals and reduceTermOnAdditionalCapitalRepayment is false,
      // adjust the term to get monthly payment closest to desiredMonthlyPayment
      if (
        this.reduceTermOnAdditionalCapitalRepayment === false &&
        !this.isInterestOnly &&
        this.principal > 0
      ) {
        this.adjustTermToDesiredPayment();
      }
    }
  }

  adjustTermToDesiredPayment() {
    // Calculate the term that would give exactly the desired monthly payment
    const exactTerm = nper(
      this.interestRate / 12 / 100,
      -this.desiredMonthlyPayment,
      this.principal,
    );

    // Round to nearest whole month
    const roundedTerm = Math.round(exactTerm);

    if (roundedTerm > 0 && roundedTerm !== this._remainingMonths) {
      this._remainingMonths = roundedTerm;
    }
  }
}
